import * as React from "react";
import { cn } from "../ui/index.ts";
import { I } from "../icons/icons.tsx";
import {
  MESSAGE_CONVERSATIONS,
  MESSAGE_THREADS,
  type ChatMessage,
} from "../../messages/conversations.ts";
import { appNavigate, registerChatDockHandler } from "../../core/app-bridge.ts";
import { paths } from "../../core/services/navigation.service.ts";
import { getCurrentPracticeId } from "../../core/services/practices.service.ts";
import { patientsService } from "../../core/services/patients.service.ts";
import { Interactions } from "../../core/providers/interactions.provider.tsx";

const STORAGE_KEY = "orthonexus-chat-dock";
const POSITION_STORAGE_KEY = "orthonexus-chat-dock-position";
const STATE_VERSION = 1;
const DRAG_THRESHOLD = 4;
const EDGE_PADDING = 8;

interface OpenChat {
  conversationId: number;
  minimized: boolean;
}

interface PersistedState {
  v: 1;
  chats: OpenChat[];
  readIds: number[];
}

interface ChatDockContextValue {
  chats: OpenChat[];
  isRead: (id: number) => boolean;
  unreadFor: (id: number) => number;
  openChat: (id: number) => void;
  closeChat: (id: number) => void;
  toggleMinimize: (id: number) => void;
}

const ChatDockContext = React.createContext<ChatDockContextValue | null>(null);

export function useChatDock(): ChatDockContextValue {
  const ctx = React.useContext(ChatDockContext);
  if (!ctx) throw new Error("useChatDock must be used inside ChatDockProvider");
  return ctx;
}

function loadState(): { chats: OpenChat[]; readIds: number[] } {
  if (typeof localStorage === "undefined") return { chats: [], readIds: [] };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { chats: [], readIds: [] };
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    if (parsed?.v !== STATE_VERSION) return { chats: [], readIds: [] };
    return { chats: parsed.chats ?? [], readIds: parsed.readIds ?? [] };
  } catch {
    return { chats: [], readIds: [] };
  }
}

function saveState(chats: OpenChat[], readIds: number[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    const payload: PersistedState = { v: STATE_VERSION, chats, readIds };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* non-fatal */
  }
}

export function ChatDockProvider({ children }: { children: React.ReactNode }) {
  const initial = React.useMemo(() => loadState(), []);
  const [chats, setChats] = React.useState<OpenChat[]>(initial.chats);
  const [readIds, setReadIds] = React.useState<number[]>(initial.readIds);

  React.useEffect(() => {
    saveState(chats, readIds);
  }, [chats, readIds]);

  // Only one conversation can be active at a time — opening replaces any existing chat.
  const openChat = React.useCallback((id: number) => {
    setChats([{ conversationId: id, minimized: false }]);
    setReadIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const closeChat = React.useCallback((id: number) => {
    setChats((prev) => prev.filter((c) => c.conversationId !== id));
  }, []);

  const toggleMinimize = React.useCallback((id: number) => {
    setChats((prev) =>
      prev.map((c) =>
        c.conversationId === id ? { ...c, minimized: !c.minimized } : c,
      ),
    );
    setReadIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const isRead = React.useCallback(
    (id: number) => readIds.includes(id),
    [readIds],
  );

  const unreadFor = React.useCallback(
    (id: number) => {
      if (readIds.includes(id)) return 0;
      return MESSAGE_CONVERSATIONS.find((c) => c.id === id)?.unread ?? 0;
    },
    [readIds],
  );

  // Register the bridge handler so appOpenMessageConversation routes here.
  React.useEffect(() => {
    registerChatDockHandler(openChat);
    return () => registerChatDockHandler(null);
  }, [openChat]);

  const value: ChatDockContextValue = {
    chats,
    isRead,
    unreadFor,
    openChat,
    closeChat,
    toggleMinimize,
  };

  return (
    <ChatDockContext.Provider value={value}>
      {children}
      <ChatDock />
    </ChatDockContext.Provider>
  );
}

interface DockPosition {
  left: number;
  top: number;
}

const LAUNCHER_SIZE = 48;

function defaultPosition(): DockPosition {
  if (typeof window === "undefined") {
    return { left: 100, top: 100 };
  }
  return {
    left: window.innerWidth - LAUNCHER_SIZE - EDGE_PADDING,
    top: window.innerHeight - LAUNCHER_SIZE - EDGE_PADDING,
  };
}

function clampPosition(p: DockPosition): DockPosition {
  if (typeof window === "undefined") return p;
  return {
    left: Math.max(
      EDGE_PADDING,
      Math.min(window.innerWidth - LAUNCHER_SIZE - EDGE_PADDING, p.left),
    ),
    top: Math.max(
      EDGE_PADDING,
      Math.min(window.innerHeight - LAUNCHER_SIZE - EDGE_PADDING, p.top),
    ),
  };
}

function loadPosition(): DockPosition {
  if (typeof localStorage === "undefined") return defaultPosition();
  try {
    const raw = localStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) return defaultPosition();
    const p = JSON.parse(raw) as Partial<DockPosition>;
    if (typeof p.left === "number" && typeof p.top === "number") {
      return clampPosition({ left: p.left, top: p.top });
    }
  } catch {
    /* fall through */
  }
  return defaultPosition();
}

function savePosition(pos: DockPosition): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(pos));
  } catch {
    /* non-fatal */
  }
}

interface DockSize {
  width: number;
  height: number;
}

const SIZE_STORAGE_KEY = "orthonexus-chat-dock-size";
const DEFAULT_SIZE: DockSize = { width: 320, height: 440 };
const MIN_SIZE: DockSize = { width: 240, height: 300 };

function clampSize(s: DockSize): DockSize {
  if (typeof window === "undefined") return s;
  return {
    width: Math.max(
      MIN_SIZE.width,
      Math.min(window.innerWidth - 2 * EDGE_PADDING, s.width),
    ),
    height: Math.max(
      MIN_SIZE.height,
      Math.min(window.innerHeight - 2 * EDGE_PADDING, s.height),
    ),
  };
}

function loadSize(): DockSize {
  if (typeof localStorage === "undefined") return DEFAULT_SIZE;
  try {
    const raw = localStorage.getItem(SIZE_STORAGE_KEY);
    if (!raw) return DEFAULT_SIZE;
    const s = JSON.parse(raw) as Partial<DockSize>;
    if (typeof s.width === "number" && typeof s.height === "number") {
      return clampSize({ width: s.width, height: s.height });
    }
  } catch {
    /* fall through */
  }
  return DEFAULT_SIZE;
}

function saveSize(s: DockSize): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* non-fatal */
  }
}

interface DockOrientation {
  horiz: "left" | "right";
  vert: "top" | "bottom";
}

function orientationFor(pos: DockPosition): DockOrientation {
  if (typeof window === "undefined") return { horiz: "right", vert: "bottom" };
  const cx = pos.left + LAUNCHER_SIZE / 2;
  const cy = pos.top + LAUNCHER_SIZE / 2;
  return {
    horiz: cx < window.innerWidth / 2 ? "left" : "right",
    vert: cy < window.innerHeight / 2 ? "top" : "bottom",
  };
}

function ChatDock() {
  const dock = useChatDock();
  const open = dock.chats.filter((c) => !c.minimized);
  const hasVisibleChat = open.length > 0;

  const [position, setPosition] = React.useState<DockPosition>(() =>
    loadPosition(),
  );
  const [size, setSize] = React.useState<DockSize>(() => loadSize());
  const [dragPos, setDragPos] = React.useState<{
    left: number;
    top: number;
  } | null>(null);
  const dockRef = React.useRef<HTMLDivElement>(null);
  const justDraggedRef = React.useRef(false);
  const animRef = React.useRef<Animation | null>(null);
  const dragRef = React.useRef<{
    source: "bubble" | "chat";
    pointerId: number;
    startX: number;
    startY: number;
    originLeft: number;
    originTop: number;
    moved: boolean;
  } | null>(null);

  React.useEffect(() => () => animRef.current?.cancel(), []);

  React.useEffect(() => {
    savePosition(position);
  }, [position]);

  React.useEffect(() => {
    saveSize(size);
  }, [size]);

  React.useEffect(() => {
    const onResize = () => {
      setPosition((p) => clampPosition(p));
      setSize((s) => clampSize(s));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const startDrag = React.useCallback(
    (source: "bubble" | "chat", e: React.PointerEvent) => {
      if (e.button !== 0) return;
      const el = dockRef.current;
      if (!el) return;
      // Cancel any running snap animation so the drag picks up from the visual position
      animRef.current?.cancel();
      animRef.current = null;
      const rect = el.getBoundingClientRect();
      dragRef.current = {
        source,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        originLeft: rect.left,
        originTop: rect.top,
        moved: false,
      };
    },
    [],
  );

  const onLauncherPointerDown = (e: React.PointerEvent<HTMLButtonElement>) =>
    startDrag("bubble", e);
  const onChatHeaderPointerDown = (e: React.PointerEvent<HTMLElement>) =>
    startDrag("chat", e);

  React.useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const s = dragRef.current;
      if (!s || s.pointerId !== e.pointerId) return;
      const dx = e.clientX - s.startX;
      const dy = e.clientY - s.startY;
      if (!s.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      s.moved = true;
      const dockW = s.source === "chat" ? size.width : LAUNCHER_SIZE;
      const dockH = s.source === "chat" ? size.height : LAUNCHER_SIZE;
      const maxLeft = window.innerWidth - dockW - EDGE_PADDING;
      const maxTop = window.innerHeight - dockH - EDGE_PADDING;
      setDragPos({
        left: Math.max(EDGE_PADDING, Math.min(maxLeft, s.originLeft + dx)),
        top: Math.max(EDGE_PADDING, Math.min(maxTop, s.originTop + dy)),
      });
    };
    const onUp = (e: PointerEvent) => {
      const s = dragRef.current;
      if (!s || s.pointerId !== e.pointerId) return;
      if (s.moved) {
        const dockW = s.source === "chat" ? size.width : LAUNCHER_SIZE;
        const dockH = s.source === "chat" ? size.height : LAUNCHER_SIZE;
        const maxLeft = window.innerWidth - dockW - EDGE_PADDING;
        const maxTop = window.innerHeight - dockH - EDGE_PADDING;
        const finalLeft = Math.max(
          EDGE_PADDING,
          Math.min(maxLeft, s.originLeft + (e.clientX - s.startX)),
        );
        const finalTop = Math.max(
          EDGE_PADDING,
          Math.min(maxTop, s.originTop + (e.clientY - s.startY)),
        );
        const centerX = finalLeft + dockW / 2;
        const centerY = finalTop + dockH / 2;
        const snapLeft =
          centerX < window.innerWidth / 2
            ? EDGE_PADDING
            : window.innerWidth - LAUNCHER_SIZE - EDGE_PADDING;
        const snapTop =
          s.source === "chat"
            ? centerY < window.innerHeight / 2
              ? EDGE_PADDING
              : window.innerHeight - LAUNCHER_SIZE - EDGE_PADDING
            : Math.max(
                EDGE_PADDING,
                Math.min(
                  window.innerHeight - LAUNCHER_SIZE - EDGE_PADDING,
                  finalTop,
                ),
              );

        // Where the dock's top-left will be after React applies the new state.
        const targetDockLeft =
          s.source === "chat"
            ? snapLeft === EDGE_PADDING
              ? EDGE_PADDING
              : window.innerWidth - dockW - EDGE_PADDING
            : snapLeft;
        const targetDockTop =
          s.source === "chat"
            ? snapTop === EDGE_PADDING
              ? EDGE_PADDING
              : window.innerHeight - dockH - EDGE_PADDING
            : snapTop;

        setPosition({ left: snapLeft, top: snapTop });

        // Animate via transform so left/top stay free for size-driven repositioning (resize).
        const el = dockRef.current;
        const dx = finalLeft - targetDockLeft;
        const dy = finalTop - targetDockTop;
        if (el && (dx !== 0 || dy !== 0)) {
          animRef.current?.cancel();
          animRef.current = el.animate(
            [
              { transform: `translate(${dx}px, ${dy}px)` },
              { transform: "translate(0, 0)" },
            ],
            {
              duration: 360,
              easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
              fill: "backwards",
            },
          );
          const anim = animRef.current;
          anim.addEventListener("finish", () => {
            if (animRef.current === anim) animRef.current = null;
          });
        }

        justDraggedRef.current = true;
        setTimeout(() => {
          justDraggedRef.current = false;
        }, 50);
      }
      dragRef.current = null;
      setDragPos(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [size]);

  const isClickSuppressed = () => justDraggedRef.current;
  const orientation = orientationFor(position);

  const viewportW = typeof window !== "undefined" ? window.innerWidth : 0;
  const viewportH = typeof window !== "undefined" ? window.innerHeight : 0;
  const dockW = hasVisibleChat ? size.width : LAUNCHER_SIZE;
  const dockH = hasVisibleChat ? size.height : LAUNCHER_SIZE;

  let containerStyle: React.CSSProperties;
  if (dragPos) {
    containerStyle = { left: dragPos.left, top: dragPos.top };
  } else if (hasVisibleChat) {
    containerStyle = {
      left:
        orientation.horiz === "left"
          ? EDGE_PADDING
          : viewportW - dockW - EDGE_PADDING,
      top:
        orientation.vert === "top"
          ? EDGE_PADDING
          : viewportH - dockH - EDGE_PADDING,
    };
  } else {
    containerStyle = {
      left:
        orientation.horiz === "left"
          ? EDGE_PADDING
          : viewportW - LAUNCHER_SIZE - EDGE_PADDING,
      top: Math.max(
        EDGE_PADDING,
        Math.min(viewportH - LAUNCHER_SIZE - EDGE_PADDING, position.top),
      ),
    };
  }
  return (
    <div
      ref={dockRef}
      data-chat-dock-root
      className="pointer-events-auto fixed z-50"
      style={containerStyle}
    >
      {open.map((c) => (
        <ChatWindow
          key={c.conversationId}
          chat={c}
          orientation={orientation}
          size={size}
          onResize={(next) => setSize(clampSize(next))}
          onHeaderPointerDown={onChatHeaderPointerDown}
          isHeaderClickSuppressed={isClickSuppressed}
        />
      ))}
      {!hasVisibleChat && (
        <ChatLauncher
          onPointerDown={onLauncherPointerDown}
          isClickSuppressed={isClickSuppressed}
          orientation={orientation}
        />
      )}
    </div>
  );
}

function ChatLauncher({
  onPointerDown,
  isClickSuppressed,
  orientation,
}: {
  onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => void;
  isClickSuppressed: () => boolean;
  orientation: DockOrientation;
}) {
  const dock = useChatDock();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const active = dock.chats[0] ?? null;

  const totalUnread = React.useMemo(
    () => MESSAGE_CONVERSATIONS.reduce((sum, conv) => sum + conv.unread, 0),
    [],
  );

  // Auto-close popover when a conversation becomes active.
  React.useEffect(() => {
    if (active) setOpen(false);
  }, [active?.conversationId]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (id: number) => {
    dock.openChat(id);
    setOpen(false);
  };

  const onLauncherClick = () => {
    if (isClickSuppressed()) return;
    if (active) {
      dock.toggleMinimize(active.conversationId);
    } else {
      setOpen((o) => !o);
    }
  };

  const launcherTitle = active
    ? active.minimized
      ? "Rouvrir la conversation"
      : "Réduire la conversation"
    : "Conversations (glisser pour déplacer)";

  return (
    <div ref={rootRef} className="pointer-events-auto relative">
      {open && (
        <div
          className={cn(
            "absolute w-64 overflow-hidden rounded-lg border border-sf-border bg-white shadow-xl",
            orientation.horiz === "right" ? "right-0" : "left-0",
            orientation.vert === "top" ? "top-14" : "bottom-14",
          )}
        >
          <div className="border-b border-sf-border px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-sf-text-muted">
            Conversations
          </div>
          <ul className="max-h-72 overflow-y-auto py-1">
            {MESSAGE_CONVERSATIONS.map((conv) => {
              const unread = dock.unreadFor(conv.id);
              return (
                <li key={conv.id}>
                  <button
                    type="button"
                    onClick={() => pick(conv.id)}
                    className="flex w-full cursor-pointer items-center gap-2.5 border-none bg-transparent px-3 py-1.5 text-left font-[inherit] hover:bg-gray-50"
                  >
                    <span
                      className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
                      style={{ background: conv.color }}
                    >
                      {conv.avatar}
                      {conv.online && (
                        <span className="absolute right-0 bottom-0 h-2 w-2 rounded-full bg-emerald-500 ring-1 ring-white" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-1.5">
                        <span className="truncate text-[12.5px] font-semibold text-sf-text">
                          {conv.name}
                        </span>
                        <span className="shrink-0 text-[10px] text-sf-text-muted">
                          {conv.time}
                        </span>
                      </span>
                      <span className="block truncate text-[11px] text-sf-text-muted">
                        {conv.preview}
                      </span>
                    </span>
                    {unread > 0 && (
                      <span className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-sf-danger px-1 text-[10px] font-semibold text-white">
                        {unread}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            onClick={() => {
              appNavigate(paths.messages(getCurrentPracticeId()));
              setOpen(false);
            }}
            className="flex w-full cursor-pointer items-center justify-center gap-1.5 border-t border-sf-border bg-sf-surface-alt px-3 py-2 text-[11.5px] font-semibold text-sf-primary hover:bg-sf-primary-50"
          >
            Voir tous les messages
            <I.ChevronRight size={11} />
          </button>
        </div>
      )}
      <button
        type="button"
        onPointerDown={onPointerDown}
        onClick={onLauncherClick}
        title={launcherTitle}
        className={cn(
          "relative inline-flex h-12 w-12 cursor-grab touch-none items-center justify-center rounded-full text-white shadow-xl transition-transform hover:scale-105 active:cursor-grabbing",
          open ? "bg-sf-primary-hover" : "bg-sf-primary",
        )}
      >
        {active && !active.minimized ? (
          <I.Minimize size={18} />
        ) : (
          <I.Message size={20} />
        )}
        {totalUnread > 0 && !open && (
          <span className="pointer-events-none absolute -top-1 -right-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-sf-danger px-1 text-[11px] font-semibold text-white ring-2 ring-white">
            {totalUnread > 9 ? "9+" : totalUnread}
          </span>
        )}
      </button>
    </div>
  );
}

function ChatWindow({
  chat,
  orientation,
  size,
  onResize,
  onHeaderPointerDown,
  isHeaderClickSuppressed,
}: {
  chat: OpenChat;
  orientation: DockOrientation;
  size: DockSize;
  onResize: (next: DockSize) => void;
  onHeaderPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  isHeaderClickSuppressed: () => boolean;
}) {
  const dock = useChatDock();
  const toast = Interactions.useToast();
  const conv = MESSAGE_CONVERSATIONS.find((c) => c.id === chat.conversationId);
  const initialMsgs = React.useMemo<ChatMessage[]>(
    () => (conv ? (MESSAGE_THREADS[conv.id] ?? []) : []),
    [conv],
  );
  const [msgs, setMsgs] = React.useState<ChatMessage[]>(initialMsgs);
  const [draft, setDraft] = React.useState("");
  const endRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [msgs.length]);

  if (!conv) return null;

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    setMsgs((prev) => [...prev, { from: "practice", text, time }]);
    setDraft("");
  };

  const call = () => {
    const patient = patientsService.findByName(
      conv.name,
      getCurrentPracticeId(),
    );
    toast.push({
      type: "info",
      title: `Appel de ${conv.name}…`,
      message: patient?.phone,
    });
  };

  // The inward-facing edges (away from the screen anchor) are the ones the user can grab.
  const handleTop = orientation.vert === "bottom";
  const handleLeft = orientation.horiz === "right";
  const cornerCursor = handleTop === handleLeft ? "nwse-resize" : "nesw-resize";

  const startResize =
    (axis: "h" | "v" | "both", cursor: string) =>
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startY = e.clientY;
      const startW = size.width;
      const startH = size.height;
      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        const dw = axis === "v" ? 0 : handleLeft ? -dx : dx;
        const dh = axis === "h" ? 0 : handleTop ? -dy : dy;
        onResize({ width: startW + dw, height: startH + dh });
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      document.body.style.cursor = cursor;
      document.body.style.userSelect = "none";
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    };

  return (
    <div
      className="pointer-events-auto relative flex flex-col overflow-hidden rounded-lg border border-sf-border bg-white shadow-xl"
      style={{ width: size.width, height: size.height }}
    >
      {/* Header */}
      <div
        onPointerDown={onHeaderPointerDown}
        className="flex shrink-0 cursor-grab touch-none items-center gap-2 border-b border-sf-border bg-white px-2 py-1.5 active:cursor-grabbing"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span
            className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
            style={{ background: conv.color }}
          >
            {conv.avatar}
            {conv.online && (
              <span className="absolute right-0 bottom-0 h-2 w-2 rounded-full bg-emerald-500 ring-1 ring-white" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12.5px] font-semibold text-sf-text">
              {conv.name}
            </div>
            <div
              className={cn(
                "text-[10.5px]",
                conv.online ? "text-emerald-600" : "text-sf-text-muted",
              )}
            >
              {conv.online ? "En ligne" : "Hors ligne"}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => {
              if (isHeaderClickSuppressed()) return;
              dock.toggleMinimize(conv.id);
            }}
            className="inline-flex h-6 w-7 items-center justify-center rounded-none text-sf-text-muted hover:bg-sf-surface-alt"
            title="Réduire"
          >
            <I.WinMinimize size={13} />
          </button>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => {
              if (isHeaderClickSuppressed()) return;
              appNavigate(paths.messages(getCurrentPracticeId(), conv.id));
              dock.closeChat(conv.id);
            }}
            className="inline-flex h-6 w-7 items-center justify-center rounded-none text-sf-text-muted hover:bg-sf-surface-alt"
            title="Agrandir dans la page Messages"
          >
            <I.WinMaximize size={11} />
          </button>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => {
              if (isHeaderClickSuppressed()) return;
              dock.closeChat(conv.id);
            }}
            className="inline-flex h-6 w-7 items-center justify-center rounded-none text-sf-text-muted hover:bg-red-500 hover:text-white"
            title="Fermer"
          >
            <I.X size={13} />
          </button>
        </div>
      </div>
      {/* Messages */}
      <div className="min-h-0 flex-1 overflow-y-auto bg-sf-surface-alt px-2.5 py-2">
        <div className="flex flex-col gap-1.5">
          {msgs.map((m, i) => {
            const isPractice = m.from === "practice";
            return (
              <div
                key={i}
                className={cn(
                  "flex max-w-[85%] flex-col gap-0.5",
                  isPractice ? "items-end self-end" : "items-start self-start",
                )}
              >
                <div
                  className={cn(
                    "px-2.5 py-1.5 text-[12.5px] leading-snug shadow-(--sf-shadow-sm)",
                    isPractice
                      ? "rounded-xl rounded-br-sm bg-sf-primary text-white"
                      : "rounded-xl rounded-bl-sm border border-sf-border bg-white text-sf-text",
                  )}
                >
                  {m.text}
                </div>
                <div
                  className={cn(
                    "px-1 text-[9.5px] text-sf-text-muted",
                    isPractice && "text-right",
                  )}
                >
                  {m.time}
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
      </div>
      {/* Input */}
      <div className="shrink-0 border-t border-sf-border bg-white p-1.5">
        <div className="flex items-end gap-1.5 rounded-md border border-sf-border bg-sf-surface-alt p-1 focus-within:border-sf-primary focus-within:shadow-[0_0_0_3px_color-mix(in_srgb,var(--sf-primary)_12%,transparent)]">
          <button
            type="button"
            onClick={call}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-sf-text-muted hover:bg-white hover:text-sf-primary"
            title={`Appeler ${conv.name}`}
          >
            <I.Phone size={13} />
          </button>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Écrire…"
            rows={1}
            className="max-h-20 min-h-5 flex-1 resize-none border-none bg-transparent font-[inherit] text-[12.5px] text-sf-text outline-none"
          />
          <button
            type="button"
            onClick={send}
            disabled={!draft.trim()}
            className={cn(
              "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded bg-sf-primary text-white",
              !draft.trim() && "cursor-not-allowed opacity-40",
            )}
          >
            <I.Send size={12} />
          </button>
        </div>
      </div>
      {/* Resize handles: inward edges and corner (invisible hit zones) */}
      <div
        onPointerDown={startResize("h", "ew-resize")}
        className={cn(
          "absolute z-20 inset-y-3 w-1.5",
          handleLeft ? "left-0" : "right-0",
        )}
        style={{ cursor: "ew-resize" }}
      />
      <div
        onPointerDown={startResize("v", "ns-resize")}
        className={cn(
          "absolute z-20 inset-x-3 h-1.5",
          handleTop ? "top-0" : "bottom-0",
        )}
        style={{ cursor: "ns-resize" }}
      />
      <div
        onPointerDown={startResize("both", cornerCursor)}
        className={cn(
          "absolute z-30 h-3 w-3",
          handleTop ? "top-0" : "bottom-0",
          handleLeft ? "left-0" : "right-0",
        )}
        style={{ cursor: cornerCursor }}
      />
    </div>
  );
}
